import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft, Check, ChevronRight, House, Bell, Menu as MenuIcon, X,
  Plus, Pencil, ListChecks, History, LogOut, Users, User,
  MessageCircle, AudioWaveform, Hand, Search,
} from "lucide-react";
import { surveyApi } from "@/lib/apiClient";
import { useAuth } from "@/lib/useAuth";
import { ENV } from "@/config/env";
import { T, hexA, fmtStamp } from "@/uniq/theme";
import { SchemaForm } from "@/uniq/Form";
import { CompanyAutocomplete } from "@/uniq/CompanyAutocomplete";
import { VoiceAnswer } from "@/uniq/VoiceAnswer";
import { SurveyDef } from "@/uniq/SurveyDef";
import { MethodIcon } from "@/uniq/methods";
import { ImpactBadge } from "@/uniq/badges";
import {
  emptyAnswerMap, fromAnswers, pruneHiddenAnswers, toAnswers, type AnswerMap,
} from "@/uniq/answers";
import { isComplete, visibleQuestions, stripCustomerNameQuestions } from "@shared-domain/survey/logic";
import {
  impactBadge, hearingOf, methodOf, methodLabelOf, HEARING_QID, METHOD_QID, IMPACTS_QID,
} from "@shared-domain/survey/presets";
import type {
  AnswerValue, Survey, SurveyListItem, SurveyResponse,
} from "@shared-domain/survey/contract";

type Cust = { id: string; name: string; no?: string; addr?: string; contact?: string; type?: "corporate" | "individual"; free?: boolean };
type Screen = "list" | "answer" | "voice" | "done" | "detail" | "announce" | "def";
type Mode = "tap" | "freerec" | "dialog";
type SortKey = "updatedDesc" | "updatedAsc" | "name";

const SORT_OPTS: { id: SortKey; label: string }[] = [
  { id: "updatedDesc", label: "最終更新日時（新しい順）" },
  { id: "updatedAsc", label: "最終更新日時（古い順）" },
  { id: "name", label: "顧客名順" },
];

// 回答値を表示文字列/チップに変換
function renderValue(survey: Survey, questionId: string, val: AnswerValue | undefined) {
  const q = survey.questions.find((x) => x.questionId === questionId);
  if (!q || val == null || val === "" || (Array.isArray(val) && !val.length))
    return <span style={{ color: "#B6C2D2" }}>—</span>;
  if (q.type === "date" || q.type === "text") return <span>{String(val)}</span>;
  if (q.type === "scale") return <span style={{ fontWeight: 700 }}>{String(val)}</span>;
  if (q.type === "radio") {
    const rv = typeof val === "object" && !Array.isArray(val) ? val.v : String(val);
    const o = q.options.find((x) => x.v === rv);
    const tone = o?.tone === "ok" ? T.ok : o?.tone === "warn" ? T.warn : "#334155";
    const label = (o?.label ?? rv) + (typeof val === "object" && !Array.isArray(val) && val.text ? `（${val.text}）` : "");
    if (o?.tone)
      return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: tone, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 9999, background: tone }} />{label}</span>;
    if (questionId === METHOD_QID)
      return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}><MethodIcon method={rv} size={15} color={T.sub} />{label}</span>;
    return <span style={{ fontWeight: 600 }}>{label}</span>;
  }
  if (q.type === "checkbox" && Array.isArray(val)) {
    const labels = val.map((v) => q.options.find((o) => o.v === v)?.label).filter(Boolean) as string[];
    // 影響項目チップは枠/背景/文字/●すべてオレンジ(回答画面の影響ありと同色)にする。
    const impactsDot = questionId === IMPACTS_QID;
    const chipColor = impactsDot ? T.warn : T.accent;
    return <span style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{labels.map((l) => <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: chipColor, background: hexA(chipColor, 0.08), border: `1px solid ${hexA(chipColor, 0.25)}`, borderRadius: 9999, padding: "3px 10px" }}>{impactsDot && <span style={{ width: 7, height: 7, borderRadius: 9999, background: T.warn, flex: "none" }} />}{l}</span>)}</span>;
  }
  return <span>{String(val)}</span>;
}

// 最後に選んだ回答方法の永続化 (プライベートブラウズ等の例外も吸収)。
const LAST_MODE_KEY = "uniq.lastMode";
const isMode = (s: unknown): s is Mode => s === "tap" || s === "dialog" || s === "freerec";
const readLastMode = (): Mode => {
  try { const s = localStorage.getItem(LAST_MODE_KEY); return isMode(s) ? s : "freerec"; }
  catch { return "freerec"; }
};
const writeLastMode = (m: Mode) => {
  try { localStorage.setItem(LAST_MODE_KEY, m); } catch { /* ignore */ }
};

// 回答方法シートの選択肢 (対話/自由/タップ)。
const MODE_OPTS: { id: Mode; label: string; sub: string; Icon: typeof MessageCircle }[] = [
  { id: "dialog", label: "対話モード", sub: "AIが1問ずつ質問", Icon: MessageCircle },
  { id: "freerec", label: "自由モード", sub: "自由に話してAIが整理", Icon: AudioWaveform },
  { id: "tap", label: "タップ回答", sub: "画面をタップして入力", Icon: Hand },
];

export default function UniQApp() {
  const { isAdmin, userName } = useAuth();
  const navigate = useNavigate();

  const [surveys, setSurveys] = useState<SurveyListItem[]>([]);
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [customers, setCustomers] = useState<Cust[]>([]);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [screen, setScreen] = useState<Screen>("list");
  // 非同期処理 (recordExtracted) が完了時点の現在画面を参照するための ref。
  const screenRef = useRef<Screen>("list");
  useEffect(() => { screenRef.current = screen; }, [screen]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"todo" | "mine" | "all">("todo");
  // 一覧の並び順 (回答済カードの既定 = 最終更新日時の降順)。
  const [sortKey, setSortKey] = useState<SortKey>("updatedDesc");
  // フリーワード絞込み (虫眼鏡トグルで開閉、閉じたらクリア)。
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const [active, setActive] = useState<Cust | null>(null);
  const [answerMap, setAnswerMap] = useState<AnswerMap>({});
  const [editing, setEditing] = useState(false);
  // ④ 音声抽出の即記録直後 (確認/修正画面に「記録済み」バナーを出す)。
  const [justRecorded, setJustRecorded] = useState(false);
  const [freeName, setFreeName] = useState("");
  // 既定モード = 最後に選んだモード (localStorage)。未回答カードタップ時のシートで選択・記憶。
  const [mode, setMode] = useState<Mode>(readLastMode);
  // 実際に記録する回答方法 (voice 由来は freerec/dialog、手入力/編集は tap)。
  const [subMode, setSubMode] = useState<Mode>("tap");
  const [modeSheet, setModeSheet] = useState(false);
  const [pending, setPending] = useState<Cust | null>(null);

  // 初期: 回答対象の調査一覧
  useEffect(() => {
    surveyApi.list("assigned")
      .then((r) => {
        setSurveys(r.surveys);
        if (r.surveys.length) setSurveyId((prev) => prev ?? r.surveys[0]!.surveyId);
      })
      .catch((e) => setError(String(e)));
  }, []);

  // 調査ごと: 設問・顧客・回答。全データを組んでから一括 set する
  // (targeted の myAssignments await 中に新 survey + 旧 customers の組で描画され、
  //  ①「あなたへの割り当てはありません」が一瞬誤表示されるのを防ぐ)。
  const loadSurveyData = useCallback(async (sid: string) => {
    const [s, custRes, respRes] = await Promise.all([
      surveyApi.get(sid), surveyApi.customers(sid).catch(() => ({ customers: [] })), surveyApi.responses(sid),
    ]);
    const master = new Map(custRes.customers.map((c) => [c.id, c]));
    let custs: Cust[];
    if (s.survey.distribution === "targeted") {
      const a = await surveyApi.myAssignments(sid);
      custs = a.assignments.map((x) => {
        const m = master.get(x.customerId);
        return { id: x.customerId, name: x.customerName || m?.name || x.customerId, no: m?.no, addr: m?.addr, contact: m?.contact, type: m?.type };
      });
    } else {
      custs = custRes.customers.map((c) => ({ id: c.id, name: c.name, no: c.no, addr: c.addr, contact: c.contact, type: c.type }));
    }
    setSurvey(s.survey);
    setResponses(respRes.responses);
    setCustomers(custs);
  }, []);

  useEffect(() => {
    if (!surveyId) return;
    loadSurveyData(surveyId).catch((e) => setError(String(e)));
  }, [surveyId, loadSurveyData]);

  const respByCust = useMemo(() => new Map(responses.map((r) => [r.customerId, r])), [responses]);
  // 実施方法の選択肢 v→label (カスタマイズされた選択肢のラベル解決。カードごとの find を避ける)。
  const methodLabels = useMemo(() => {
    const mq = survey?.questions.find((q) => q.questionId === METHOD_QID);
    return new Map(mq?.type === "radio" ? mq.options.map((o) => [o.v, o.label]) : []);
  }, [survey]);
  const doneCount = customers.filter((c) => respByCust.has(c.id)).length;
  const remain = customers.length - doneCount;

  // ③ 個人(individual)顧客は本人がヒアリング相手なので、顧客名で事前充填する (編集可)。
  const prefillFor = (s: Survey, c: Cust | null): AnswerMap =>
    c && !c.free && c.type === "individual" && c.name &&
    s.questions.some((q) => q.questionId === HEARING_QID)
      ? { [HEARING_QID]: c.name }
      : {};

  // 指定モードで回答開始 (タップは記入画面、音声2モードは音声画面 → 抽出後に確認)。
  const startWith = (c: Cust, m: Mode) => {
    if (!survey) return;
    setActive(c);
    setAnswerMap({ ...emptyAnswerMap(survey), ...prefillFor(survey, c) });
    setEditing(false);
    setJustRecorded(false);
    setSubMode(m);
    setScreen(m === "tap" ? "answer" : "voice");
  };
  // 未回答カードタップ → モード選択シートを開く。
  const openModeSheet = (c: Cust) => { setPending(c); setModeSheet(true); };
  const chooseMode = (m: Mode) => {
    setMode(m);
    writeLastMode(m);
    setModeSheet(false);
    if (pending) startWith(pending, m);
    setPending(null);
  };
  const startEdit = (c: Cust, resp: SurveyResponse) => {
    setActive(c);
    setAnswerMap(fromAnswers(resp.answers));
    setEditing(true);
    setJustRecorded(false);
    setSubMode("tap");
    setScreen("answer");
  };
  // ⑥ リスト外の自由追加もモード選択シート (対話/自由/タップ) を経由する。
  const startFree = () => {
    if (!survey) return;
    setFreeName("");
    openModeSheet({ id: "free" + Date.now(), name: "", free: true });
  };

  const submit = async () => {
    if (!survey || !active || !surveyId) return;
    setError(null);
    try {
      await surveyApi.respond(surveyId, {
        customerId: active.id,
        answers: toAnswers(answerMap),
        answerMode: subMode,
        ...(active.free ? { freeCustomer: { name: freeName.trim() } } : {}),
      });
      await loadSurveyData(surveyId);
      setJustRecorded(false);
      setScreen("done");
    } catch (e) {
      setError(String(e));
    }
  };

  // ④ 音声抽出の完了と同時に即記録する (Unizoと同じ「まず保存」。離脱しても回答が消えない)。
  // 保存後は回答詳細画面へ (⑦。修正したい場合は詳細の「この回答を編集する」から)。
  // 保存失敗時は従来の確認フローにフォールバック。
  // 非同期完了時にユーザが既に voice 画面を離れていたら画面遷移はしない (記録自体は有効)。
  const recordExtracted = async (pruned: AnswerMap) => {
    setAnswerMap(pruned);
    if (survey && active && surveyId && !active.free) {
      try {
        await surveyApi.respond(surveyId, {
          customerId: active.id,
          answers: toAnswers(pruned),
          answerMode: subMode,
        });
        await loadSurveyData(surveyId);
        if (screenRef.current !== "voice") return;
        setEditing(false);
        setJustRecorded(true);
        setScreen("detail");
        return;
      } catch (e) {
        setError(String(e));
      }
    }
    if (screenRef.current !== "voice") return;
    setEditing(false);
    setJustRecorded(false);
    setScreen("answer");
  };

  const q = query.trim().toLowerCase();
  const filtered = customers.filter((c) => {
    const done = respByCust.has(c.id);
    if (filter === "todo" && done) return false;
    if (filter === "mine" && !done) return false; // 記名フィルタは簡略（自分の回答は done サブで表示）
    if (!q) return true;
    const resp = respByCust.get(c.id);
    return [c.name, c.no, c.addr, resp ? hearingOf(resp) : ""]
      .filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  // 並び替え: 顧客名順は全カード対象。更新日時順は日時を持たない未回答カードを
  // 「最も古い」扱いにして全体を単一ソート（「すべて」タブでも並び順が一貫する）。同時刻/未回答同士は名前順。
  const shown = (() => {
    if (sortKey === "name")
      return [...filtered].sort((a, b) => a.name.localeCompare(b.name, "ja"));
    const stamp = (c: Cust) => respByCust.get(c.id)?.updatedAt ?? "";
    return [...filtered].sort((a, b) => {
      const sa = stamp(a);
      const sb = stamp(b);
      if (sa !== sb) return sortKey === "updatedAsc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
      return a.name.localeCompare(b.name, "ja");
    });
  })();

  // ① 指名アンケートで自分への割り当てゼロ (targeted の customers は自分の割当のみ)。
  const noAssignment = !!survey && survey.distribution === "targeted" && customers.length === 0;

  // ── screens ───────────────────────────────────────────
  // 対象顧客名の重複設問を除いた設問セット (回答フォーム/完了判定/詳細で共用・項目9)。
  const formSurvey = survey ? { ...survey, questions: stripCustomerNameQuestions(survey.questions) } : null;
  const complete = formSurvey ? isComplete(formSurvey.questions, toAnswers(answerMap)) : false;
  const freeOk = !active?.free || freeName.trim().length > 0;

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", background: T.bg, fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
      {error && <div style={{ background: "#FEF2F2", color: "#B91C1C", fontSize: 12, padding: "6px 12px" }}>{error}</div>}

      {screen === "list" && (
        <>
          <div style={{ background: "#fff", padding: "14px 16px 8px" }}>
            <span style={{ fontSize: 25, fontStyle: "italic", fontWeight: 900, color: T.accent, letterSpacing: -0.6 }}>UniQ</span>
          </div>
          {surveys.length > 1 && (
            <div style={{ background: "#fff", display: "flex", gap: 8, padding: "0 16px 10px", overflowX: "auto" }}>
              {surveys.map((s) => {
                const on = s.surveyId === surveyId;
                return (
                  <button key={s.surveyId} onClick={() => setSurveyId(s.surveyId)}
                    style={{ flex: "none", display: "flex", alignItems: "center", gap: 6, background: on ? "#fff" : "transparent", border: on ? `1.5px solid ${T.accent}` : "1px solid #DCE3EC", borderRadius: 9999, padding: "5px 11px", cursor: "pointer" }}>
                    <span style={{ width: 7, height: 7, borderRadius: 9999, background: s.tagColor || T.accent }} />
                    <span style={{ fontSize: 12, fontWeight: on ? 700 : 600, color: on ? T.ink : T.sub, whiteSpace: "nowrap" }}>{s.title}</span>
                  </button>
                );
              })}
            </div>
          )}
          {survey && (
            <div style={{ background: "#fff", padding: "0 16px 16px", borderBottom: `1px solid ${T.line}` }}>
              <div style={{ background: `linear-gradient(180deg, ${hexA(T.accent, 0.1)}, ${hexA(T.accent, 0.03)})`, border: `1px solid ${hexA(T.accent, 0.18)}`, borderRadius: 12, padding: "9px 13px" }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ok, marginBottom: 3, letterSpacing: 0.2 }}>実施中のアンケート</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setScreen("def")} title="アンケート内容を見る"
                    style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 4, background: "transparent", border: 0, padding: 0, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{survey.title}</span>
                    <ChevronRight size={15} color={T.accent} style={{ flex: "none" }} />
                  </button>
                  {survey.deadline && <span style={{ fontSize: 11, color: T.sub, whiteSpace: "nowrap" }}>期日 <b style={{ color: "#475569" }}>{survey.deadline.slice(5)}</b></span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                  <span style={{ fontSize: 10.5, color: T.sub }}>残り <b style={{ fontSize: 17, color: T.accent }}>{remain}</b> 社</span>
                  <div style={{ flex: 1, height: 6, background: "#E2E9F3", borderRadius: 9999, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (customers.length ? (doneCount / customers.length) * 100 : 0) + "%", background: T.accent }} />
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 700, color: T.sub }}>{doneCount}/{customers.length}</span>
                </div>
              </div>
            </div>
          )}
          <div style={{ background: T.bg, padding: "10px 16px 10px" }}>
            <div style={{ display: "flex", borderBottom: "1px solid #E2E8F0" }}>
              {([["todo", "未回答"], ["mine", "回答済"], ["all", "すべて"]] as const).map(([id, label]) => {
                const on = filter === id;
                return (
                  <button key={id} onClick={() => setFilter(id)} style={{ flex: 1, background: "transparent", border: 0, padding: "12px 2px", cursor: "pointer", borderBottom: on ? `3px solid ${T.accent}` : "3px solid transparent" }}>
                    <span style={{ fontSize: 16, fontWeight: on ? 800 : 600, color: on ? T.accent : T.faint }}>{label}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 10 }}>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} aria-label="並び順"
                style={{ fontSize: 12, fontWeight: 600, color: T.sub, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 8px", cursor: "pointer" }}>
                {SORT_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <button onClick={() => { setSearchOpen((v) => { if (v) setQuery(""); return !v; }); }}
                title="フリーワード絞込み" aria-label="フリーワード絞込み" aria-expanded={searchOpen}
                style={{ marginLeft: "auto", width: 32, height: 32, borderRadius: 9999, background: searchOpen ? hexA(T.accent, 0.12) : "#fff", border: searchOpen ? `1.5px solid ${T.accent}` : "1px solid #E2E8F0", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                <Search size={15} color={searchOpen ? T.accent : T.sub} />
              </button>
            </div>
            {searchOpen && (
              <div style={{ position: "relative", paddingTop: 10 }}>
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="顧客名・番号・住所・ヒアリング相手で絞込み"
                  style={{ width: "100%", boxSizing: "border-box", fontSize: 13, color: T.ink, background: "#fff", border: `1.5px solid ${T.accent}`, borderRadius: 9999, padding: "9px 38px 9px 14px", outline: "none" }} />
                {query && (
                  <button onClick={() => setQuery("")} title="クリア" aria-label="クリア"
                    style={{ position: "absolute", right: 6, top: 15, width: 26, height: 26, borderRadius: 9999, background: "transparent", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <X size={15} color={T.faint} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "4px 16px calc(76px + env(safe-area-inset-bottom))", background: T.bg }}>
            {surveys.length === 0 && <div style={{ textAlign: "center", color: "#B6C2D2", fontSize: 13, padding: "40px 0" }}>回答できる調査はありません</div>}
            {/* ① 指名アンケートで自分への割り当てがない場合の案内。 */}
            {noAssignment && (
              <div style={{ textAlign: "center", padding: "36px 16px" }}>
                <div style={{ width: 52, height: 52, borderRadius: 9999, background: T.line, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Users size={24} color={T.faint} /></div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.sub, marginTop: 12 }}>あなたへの割り当てはありません</div>
                <div style={{ fontSize: 12, color: T.faint, marginTop: 6 }}>このアンケートは指名制です。本部から割り当てられるとここに表示されます。</div>
              </div>
            )}
            {/* ② 未回答タブで全件回答済みの完了メッセージ / 絞込み0件の案内。 */}
            {!noAssignment && customers.length > 0 && shown.length === 0 && (
              q ? (
                <div style={{ textAlign: "center", color: "#B6C2D2", fontSize: 13, padding: "28px 0" }}>該当する顧客がありません</div>
              ) : filter === "todo" ? (
                <div style={{ textAlign: "center", padding: "32px 16px" }}>
                  <div style={{ width: 52, height: 52, borderRadius: 9999, background: hexA(T.ok, 0.12), display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Check size={26} color={T.ok} strokeWidth={3} /></div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.ok, marginTop: 12 }}>すべて回答済みです</div>
                  <div style={{ fontSize: 12.5, color: T.sub, marginTop: 6 }}>お疲れさまでした！ 回答内容は「回答済」タブから確認・編集できます。</div>
                </div>
              ) : filter === "mine" ? (
                <div style={{ textAlign: "center", color: "#B6C2D2", fontSize: 13, padding: "28px 0" }}>回答済のアンケートはありません</div>
              ) : null
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {shown.map((c) => {
                const resp = respByCust.get(c.id);
                if (resp) return <DoneCard key={c.id} c={c} resp={resp} methodLabels={methodLabels} onOpen={() => { setActive(c); setScreen("detail"); }} />;
                return (
                  <button key={c.id} onClick={() => openModeSheet(c)}
                    style={{ width: "100%", textAlign: "left", background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.05)", padding: "10px 12px", border: "1px solid #EEF2F7", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: T.faint, background: "#F1F5F9", borderRadius: 9999, padding: "1px 7px" }}>未回答</span>
                        <span style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{c.name}</span>
                      </div>
                      {(c.no || c.addr) && <div style={{ fontSize: 11, color: T.faint, marginTop: 3, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{c.no}{c.no && c.addr ? " ・ " : ""}{c.addr}</div>}
                    </div>
                    <span style={{ display: "flex", alignItems: "center", gap: 2, background: hexA(T.accent, 0.1), color: T.accent, borderRadius: 9999, padding: "7px 10px 7px 13px", fontSize: 12.5, fontWeight: 700 }}>回答<ChevronRight size={14} color={T.accent} /></span>
                  </button>
                );
              })}
              {/* ③ 回答済タブでは非表示 (回答済一覧に「追加して回答」があるのは不自然)。①の割当ゼロ時も非表示。 */}
              {survey && filter !== "mine" && !noAssignment && (
                <button onClick={startFree} style={{ marginTop: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "transparent", border: "1.5px dashed #CBD5E1", borderRadius: 14, padding: "13px", fontSize: 13, fontWeight: 600, color: T.sub, cursor: "pointer" }}>
                  <Plus size={16} color={T.faint} />リストにない先を追加して回答
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {screen === "def" && survey && (
        <SurveyDef survey={survey} onBack={() => setScreen("list")} />
      )}

      {screen === "announce" && (
        <>
          <div style={{ background: "#fff", padding: "16px 16px 12px", borderBottom: `1px solid ${T.line}` }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>お知らせ</div>
            <div style={{ fontSize: 11, color: T.faint, marginTop: 2 }}>本部・管理部からの通達</div>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "40px 16px calc(76px + env(safe-area-inset-bottom))", textAlign: "center", color: "#B6C2D2", fontSize: 13 }}>お知らせはありません</div>
        </>
      )}

      {screen === "answer" && survey && active && (
        <>
          <div style={{ background: "#fff", padding: "8px 12px 11px", borderBottom: `1px solid ${T.line}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
              <button onClick={() => { setJustRecorded(false); setScreen(editing ? "detail" : "list"); }} style={{ width: 34, height: 34, borderRadius: 9999, background: "transparent", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowLeft size={20} color={T.ink} /></button>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.ink }}>{editing ? "回答を編集" : "アンケート回答"}</div>
            </div>
            {active.free ? (
              <CompanyAutocomplete value={freeName} onChange={setFreeName} autoFocus placeholder="対象顧客名を入力（企業検索・G-Biz）" />
            ) : (
              <div style={{ background: "#EAF1F0", borderRadius: 10, padding: "9px 12px" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{active.name}</div>
                {(active.no || active.contact) && <div style={{ fontSize: 11, color: T.sub, marginTop: 1 }}>{active.no}{active.no && active.contact ? " ・ " : ""}{active.contact}</div>}
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "18px 16px 24px" }}>
            <SchemaForm survey={formSurvey ?? survey} answers={answerMap} onChange={(qid, v) => setAnswerMap((m) => pruneHiddenAnswers(survey, { ...m, [qid]: v }))} />
          </div>
          <div style={{ background: "#fff", borderTop: `1px solid ${T.line}`, padding: "10px 16px 14px" }}>
            {!complete && <div style={{ fontSize: 11, color: T.faint, textAlign: "center", marginBottom: 8 }}>必須項目をすべて入力すると実施完了になります</div>}
            <button disabled={!complete || !freeOk} onClick={submit}
              style={{ width: "100%", height: 52, borderRadius: 9999, background: complete && freeOk ? T.accent : "#D8E0EA", color: "#fff", border: 0, fontSize: 15, fontWeight: 700, cursor: complete && freeOk ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Check size={18} color="#fff" strokeWidth={3} />{editing ? "変更を保存" : "回答を記録"}
            </button>
          </div>
        </>
      )}

      {screen === "voice" && survey && active && surveyId && (
        <VoiceAnswer
          survey={survey}
          surveyId={surveyId}
          mode={mode === "dialog" ? "dialog" : "freerec"}
          customer={{ name: active.name, no: active.no, addr: active.addr, contact: active.contact }}
          prefill={prefillFor(survey, active)}
          onExtracted={(ans) => {
            void recordExtracted(pruneHiddenAnswers(survey, { ...emptyAnswerMap(survey), ...prefillFor(survey, active), ...fromAnswers(ans) }));
          }}
          onBack={() => setScreen("list")}
        />
      )}

      {screen === "done" && survey && active && (
        <>
          <div style={{ flex: 1, overflow: "auto" }}>
            <div style={{ background: `linear-gradient(180deg, ${hexA(T.accent, 0.14)}, ${hexA(T.accent, 0)})`, padding: "30px 16px 18px", textAlign: "center" }}>
              <div style={{ width: 64, height: 64, borderRadius: 9999, background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", boxShadow: `0 8px 20px -4px ${hexA(T.accent, 0.5)}` }}><Check size={34} color="#fff" strokeWidth={3} /></div>
              <div style={{ fontSize: 19, fontWeight: 800, color: T.ink }}>回答を記録しました</div>
              <div style={{ fontSize: 12.5, color: T.sub, marginTop: 6 }}>{survey.title} ・ 残り <b style={{ color: T.accent }}>{remain}社</b></div>
            </div>
            <div style={{ padding: "4px 16px 16px" }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.faint }}>記録した内容</span>
              </div>
              <Summary survey={survey} customerName={active.name} answers={answerMap} />
            </div>
          </div>
          {/* 進む方 (続けて回答する) が右・確認 (回答済一覧) が左の2ボタン。 */}
          <div style={{ background: "#fff", borderTop: `1px solid ${T.line}`, padding: "12px 16px 14px", display: "flex", gap: 10 }}>
            <button onClick={() => { setFilter("mine"); setScreen("list"); }} style={{ flex: 1, height: 52, borderRadius: 9999, background: "#fff", color: "#475569", border: "1px solid #E2E8F0", fontSize: 13.5, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}><ListChecks size={15} color={T.sub} />回答結果を確認する</button>
            <button onClick={() => { setFilter("todo"); setScreen("list"); }} style={{ flex: 1, height: 52, borderRadius: 9999, background: T.accent, color: "#fff", border: 0, fontSize: 13.5, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>続けて回答する<ChevronRight size={16} color="#fff" /></button>
          </div>
        </>
      )}

      {screen === "detail" && survey && active && respByCust.get(active.id) && (
        <>
          <div style={{ background: "#fff", padding: "10px 12px", display: "flex", alignItems: "center", gap: 6, borderBottom: `1px solid ${T.line}` }}>
            <button onClick={() => { setJustRecorded(false); setScreen("list"); }} style={{ width: 36, height: 36, borderRadius: 9999, background: "transparent", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowLeft size={20} color={T.ink} /></button>
            <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.ink }}>回答の詳細</div>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: T.ok, background: hexA(T.ok, 0.1), borderRadius: 9999, padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: 4 }}><Check size={11} color={T.ok} strokeWidth={3} />実施済</span>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 24px" }}>
            {justRecorded && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: hexA(T.ok, 0.1), border: `1px solid ${hexA(T.ok, 0.3)}`, borderRadius: 10, padding: "9px 12px", marginBottom: 12, fontSize: 12.5, color: T.ok, fontWeight: 600 }}>
                <Check size={15} color={T.ok} strokeWidth={3} style={{ flex: "none" }} />回答を記録しました。修正する場合は「この回答を編集する」から。
              </div>
            )}
            <Summary survey={survey} customerName={active.name} answers={fromAnswers(respByCust.get(active.id)!.answers)} />
            <HistoryBlock resp={respByCust.get(active.id)!} />
            <div style={{ fontSize: 11, color: T.faint, textAlign: "center", marginTop: 12 }}>ワークスペースの誰でも編集できます。</div>
          </div>
          <div style={{ background: "#fff", borderTop: `1px solid ${T.line}`, padding: "12px 16px 14px" }}>
            <button onClick={() => startEdit(active, respByCust.get(active.id)!)} style={{ width: "100%", height: 52, borderRadius: 9999, background: "#fff", color: T.accent, border: `1.5px solid ${T.accent}`, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Pencil size={17} color={T.accent} />この回答を編集する</button>
          </div>
        </>
      )}

      {(screen === "list" || screen === "announce") && (
        /* 固定フッタ (Unizo MobileTabBar の fixed bottom-0 + safe-area 準拠)。absolute だと
           モバイルのアドレスバー分だけ初期表示で画面外に落ちるため viewport 固定にする。 */
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "calc(64px + env(safe-area-inset-bottom))", paddingBottom: "env(safe-area-inset-bottom)", boxSizing: "border-box", background: "#fff", borderTop: `1px solid ${T.line}`, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", alignItems: "center", justifyItems: "center", zIndex: 60 }}>
          {([["list", House, "ホーム"], ["announce", Bell, "お知らせ"], ["menu", MenuIcon, "メニュー"]] as const).map(([id, Ic, label]) => {
            const on = id === "menu" ? menuOpen : (id === screen && !menuOpen);
            return (
              <button key={id} onClick={() => (id === "menu" ? setMenuOpen(true) : setScreen(id))} style={{ background: "transparent", border: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "8px 2px" }}>
                <Ic size={21} color={on ? T.accent : T.faint} />
                <span style={{ fontSize: 10, color: on ? T.accent : T.faint, fontWeight: on ? 700 : 400 }}>{label}</span>
              </button>
            );
          })}
        </div>
      )}

      {modeSheet && pending && (
        <div onClick={() => { setModeSheet(false); setPending(null); }} style={{ position: "absolute", inset: 0, zIndex: 95, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", width: "100%", borderRadius: "20px 20px 0 0", padding: "16px 20px 22px", boxShadow: "0 -10px 30px rgba(0,0,0,.18)" }}>
            <div style={{ fontSize: 11, color: T.faint, fontWeight: 600 }}>回答方法を選択</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginTop: 2, marginBottom: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pending.name || "リスト外の先"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {MODE_OPTS.map((o) => (
                <button key={o.id} onClick={() => chooseMode(o.id)} style={{ width: "100%", textAlign: "left", background: "#fff", border: mode === o.id ? `1.5px solid ${T.accent}` : "1px solid #E2E8F0", borderRadius: 14, padding: "13px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: hexA(T.accent, 0.1), display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><o.Icon size={18} color={T.accent} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>{o.label}{mode === o.id && <span style={{ fontSize: 10.5, color: T.accent, fontWeight: 700, marginLeft: 6 }}>前回</span>}</div>
                    <div style={{ fontSize: 11.5, color: T.faint, marginTop: 1 }}>{o.sub}</div>
                  </div>
                  <ChevronRight size={16} color="#CBD5E1" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: "absolute", inset: 0, zIndex: 95, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", width: "100%", borderRadius: "20px 20px 0 0", padding: "14px 20px 22px", maxHeight: "88%", overflow: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ fontSize: 17, fontWeight: 800, color: T.ink }}>メニュー</span>
              <button onClick={() => setMenuOpen(false)} style={{ width: 32, height: 32, borderRadius: 9999, background: "transparent", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={20} color={T.faint} /></button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 16, borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ width: 44, height: 44, borderRadius: 9999, background: `linear-gradient(135deg, ${T.accent}, #2D9A8F)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 17, fontWeight: 700 }}>{(userName || "U").charAt(0)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{userName}</div>
                <div style={{ fontSize: 11.5, color: T.faint, marginTop: 1 }}>{isAdmin ? "本部（管理者）" : "営業担当"}</div>
              </div>
            </div>
            {isAdmin && (
              <button onClick={() => navigate("/admin")} style={{ width: "100%", textAlign: "left", marginTop: 12, background: "transparent", border: 0, padding: "12px 4px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}><Users size={17} color={T.sub} /></div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>管理者画面</div><div style={{ fontSize: 11, color: T.faint }}>アンケート作成・集計</div></div>
                <ChevronRight size={16} color="#CBD5E1" />
              </button>
            )}
            <button onClick={() => { if (ENV.AUTH_PORTAL_URL) window.location.href = ENV.AUTH_PORTAL_URL; }} style={{ width: "100%", textAlign: "left", background: "transparent", border: 0, padding: "12px 4px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(224,85,107,.1)", display: "flex", alignItems: "center", justifyContent: "center" }}><LogOut size={17} color={T.danger} /></div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.danger }}>ログアウト</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// 回答済カード: 影響有無バッジ + 会社名 + [人]ヒアリング相手 |[実施方法] + 回答日時/実施者。
function DoneCard({ c, resp, methodLabels, onOpen }: { c: Cust; resp: SurveyResponse; methodLabels: Map<string, string>; onOpen: () => void }) {
  const badge = impactBadge(resp);
  const hearing = hearingOf(resp);
  const method = methodOf(resp);
  // 既定4択以外 (本部が実施方法の選択肢を編集した場合) は設問定義由来の map からラベルを解決する。
  const methodLbl = methodLabelOf(method) ?? (method ? methodLabels.get(method) : undefined);
  const at = fmtStamp(resp.submittedAt);
  const by = resp.submittedByName || resp.submittedBy;
  const grey = "#94A3B8";
  return (
    <button onClick={onOpen}
      style={{ width: "100%", textAlign: "left", background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,.05)", padding: "11px 12px", border: "1px solid #EEF2F7", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.accent, background: hexA(T.accent, 0.1), borderRadius: 9999, padding: "2px 8px", display: "inline-flex", alignItems: "center", gap: 3 }}><Check size={10} color={T.accent} strokeWidth={3} />回答済</span>
          {badge && <ImpactBadge kind={badge} />}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginTop: 5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
        {(hearing || methodLbl) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, overflow: "hidden" }}>
            {hearing && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: T.sub, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}><User size={13} color={grey} style={{ flex: "none" }} />{hearing}</span>}
            {hearing && methodLbl && <span style={{ width: 1, height: 12, background: "#E2E8F0", flex: "none" }} />}
            {methodLbl && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: T.sub, flex: "none" }}><MethodIcon method={method} size={13} color={grey} />{methodLbl}</span>}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 5 }}>
          <span style={{ fontSize: 10.5, color: "#B6C2D2", fontVariantNumeric: "tabular-nums" }}>{at}</span>
          {by && <span style={{ fontSize: 10.5, color: "#B6C2D2", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>by {by}</span>}
        </div>
      </div>
      <ChevronRight size={16} color="#CBD5E1" style={{ flex: "none" }} />
    </button>
  );
}

function Summary({ survey, customerName, answers, onEdit }: {
  survey: Survey; customerName: string; answers: AnswerMap; onEdit?: () => void;
}) {
  const vis = visibleQuestions(stripCustomerNameQuestions(survey.questions), toAnswers(answers));
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "4px 14px", boxShadow: "0 2px 8px rgba(0,0,0,.04)" }}>
      <div style={{ padding: "13px 4px", borderBottom: "1px solid #F1F5F9", display: "flex", gap: 12 }}>
        <span style={{ fontSize: 12, color: T.faint, width: 104, flex: "none" }}>対象顧客名</span>
        <span style={{ fontSize: 14, color: T.ink, fontWeight: 700 }}>{customerName || "—"}</span>
      </div>
      {vis.map((q) => (
        <button key={q.questionId} onClick={onEdit} style={{ width: "100%", textAlign: "left", background: "transparent", border: 0, padding: "13px 4px", borderBottom: "1px solid #F1F5F9", display: "flex", gap: 12, cursor: onEdit ? "pointer" : "default" }}>
          <span style={{ fontSize: 12, color: T.faint, width: 104, flex: "none" }}>{q.label}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: T.ink, fontWeight: 500 }}>{renderValue(survey, q.questionId, answers[q.questionId])}</span>
          {onEdit && <Pencil size={13} color="#CBD5E1" style={{ flex: "none" }} />}
        </button>
      ))}
    </div>
  );
}

function HistoryBlock({ resp }: { resp: SurveyResponse }) {
  if (!resp.history.length) return null;
  return (
    <div style={{ marginTop: 14, background: "#fff", borderRadius: 14, padding: "12px 14px", boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}><History size={13} color={T.faint} /><span style={{ fontSize: 11.5, fontWeight: 700, color: T.faint }}>更新履歴</span></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {resp.history.map((h, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: 9999, background: i === resp.history.length - 1 ? T.accent : "#CBD5E1", flex: "none" }} />
            <span style={{ fontSize: 11.5, color: T.sub, flex: 1 }}><b style={{ color: "#334155" }}>{h.byName || h.by}</b> が {h.act}</span>
            <span style={{ fontSize: 10.5, color: "#B6C2D2" }}>{h.at.slice(0, 16).replace("T", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
